import type { ActionDrawerStatus, ActionFieldSpec, ActionFieldValue } from "@ando/ui";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

/**
 * Active action store — holds the currently surfaced HITL action proposals.
 *
 * v1 stored proposals inside `ConversationContext` (1576 LOC monolith);
 * here we factor it out so the Drawer surface can mount across any routed
 * workspace tab without a chat-context coupling.
 *
 * Multi-proposal model: when Ando proposes 2 actions in a row, both stack
 * (collapsable) in the destination tab. Confirmation is independent.
 */

/**
 * Per-change overlay metadata for document-class proposals. Drives the
 * in-editor preview surfaces:
 *   - DOCX/XLSX: still routed through `proposalsStore` (chip / cell).
 *   - PPTX: read DIRECTLY off `ActiveAction.documentEdit.changes` — no
 *     separate `proposalsStore` entry. Each `change` carries
 *     `slide_index` + optional `bbox` (normalised slide-relative coords
 *     0..1) so `PptxViewerInline` can paint the bounding-box overlay.
 *
 * The shape is intentionally `unknown[]` (forwarded from the BE
 * ``DocumentEditPreview.changes``) — consumers narrow per-op.
 */
export interface ActiveActionDocumentEdit {
  readonly documentId: string;
  readonly documentTitle?: string;
  readonly documentType?: string;
  readonly minioBucket?: string | null;
  readonly minioKey?: string | null;
  /** Per-change list with optional `bbox` for pptx ops. */
  readonly changes?: ReadonlyArray<Record<string, unknown>>;
}

/**
 * Document-create preview metadata — populated for ``document_create``
 * proposals only. Mirrors the BE ``DocumentCreatePreview`` model
 * (``src/models/action_models.py``):
 *
 *   - ``title`` — filename with extension (e.g. "Contratto_X.docx")
 *   - ``documentType`` — "document" / "spreadsheet" / "presentation" /
 *     "csv" / "pdf" / "html"
 *   - ``contentPreview`` — human-readable markdown summary of the file
 *     contents the agent will materialise
 *   - ``availableDestinations`` — save targets the user can pick from
 *     based on their connected providers (always at least
 *     ``["download", "minio"]``)
 *
 * Drives the ``DocumentCreatePreview`` component rendered inside the
 * ``FocusedActionView`` body — without it, the proposal canvas is empty
 * and the user has no clue what they're about to confirm.
 */
export interface ActiveActionDocumentCreate {
  readonly title: string;
  readonly documentType: string;
  readonly contentType?: string;
  readonly contentPreview: string;
  readonly availableDestinations: ReadonlyArray<string>;
}

export interface ActiveAction {
  /** Server-issued proposal id (UUID). */
  id: string;
  /** BE `action_type` literal — drives `resolveTabForActionType`. */
  actionType: string;
  /** Conversation that birthed the proposal. */
  conversationId?: string;
  /**
   * The chat ``query_id`` of the user turn that triggered this
   * proposal. Used to render an inline action chip below the agent
   * answer in the chat (the user explicitly asked May 24, 2026 —
   * "come capisco quale doc è stato creato con quella domanda?").
   * Matches ``ChatMessageView.queryId`` so the chip can be located
   * once per message without scanning the entire action list.
   */
  queryId?: string;
  /** Vendor label for the header subtitle. */
  vendorLabel?: string;
  /** Human title (BE-supplied). */
  title: string;
  /** Optional human subtitle (entity affected). */
  subtitle?: string;
  /** Schema describing editable fields. */
  schema: readonly ActionFieldSpec[];
  /** What Ando proposed — immutable. */
  proposedFields: Readonly<Record<string, ActionFieldValue>>;
  /** Local edits (parent-controlled — used for re-hydration). */
  editedFields?: Readonly<Record<string, ActionFieldValue>>;
  /** Optional reasoning trace lines. */
  reasoning?: readonly string[];
  /** Source citations. */
  citations?: ReadonlyArray<{ id: string; label: string }>;
  /**
   * Optional nested ``document_edit`` from the BE wire — populated for
   * ``document_edit`` / ``document_fork_edit`` / ``document_create``
   * action types. Drives the pptx overlay layer (see
   * `PptxViewerInline`) and any future doc-class preview surfaces.
   */
  documentEdit?: ActiveActionDocumentEdit;
  /**
   * Optional nested ``document_create`` from the BE wire — populated
   * for ``document_create`` only. Drives the preview surface so the
   * user sees title + content summary BEFORE clicking Conferma.
   */
  documentCreate?: ActiveActionDocumentCreate;
  /**
   * True when the document was AUTHORED IN THE SANDBOX (the proposal carries
   * ``precomputed_result_key``): the previewed bytes ARE the document, so the
   * style/template picker is hidden — a pick can't be honored without
   * re-authoring (the BE ignores it and keeps the previewed bytes).
   */
  sandboxAuthored?: boolean;
  /**
   * Execute-time result payload (BE ``ExecuteActionResponse.result_data``).
   * Populated after the user clicks Conferma + the BE materialises the
   * action. For ``document_create`` this carries
   * ``{ doc_id, minio_bucket, minio_key, access_url, message }`` so the
   * chip in chat history can deep-link to the generated file even after
   * the user reloads the desktop app (paired with the ``persist``
   * middleware below). Opaque map — consumers narrow per action_type.
   */
  executedData?: Readonly<Record<string, unknown>>;
  /** Live state machine status. */
  status: ActionDrawerStatus;
  /** Latest error message when `status === "error"`. */
  error?: string | null;
  /**
   * Machine-readable failure classification (Jul 2026 HITL lifecycle) mirrored
   * from the authoritative HTTP ``ExecuteActionResponse.error_kind`` — drives
   * the card's inline recovery (reconnect / top-up credit / retry / field
   * error). Absent until an execute fails; cleared on the next execute attempt.
   */
  errorKind?: string | null;
  /** Reconnect hints for the recovery CTA (e.g. ``{ account_label, provider }``). */
  errorMeta?: Readonly<Record<string, unknown>> | null;
  /** ISO timestamp when the proposal was created. */
  createdAt: string;
  /**
   * ISO timestamp when the server-side proposal TTL expires (24h). Past this
   * the BE rejects ``execute`` ("Proposta scaduta"), so the Accept/Conferma
   * controls are gated off it. ``null``/absent = never expires (manual actions).
   */
  expiresAt?: string | null;
}

interface ActiveActionState {
  actions: Record<string, ActiveAction>;
  order: string[];
  upsert: (action: ActiveAction) => void;
  patch: (id: string, patch: Partial<ActiveAction>) => void;
  remove: (id: string) => void;
  clear: () => void;
}

/**
 * Persistence — Zustand persist middleware backs the store with
 * ``localStorage`` so executed proposals (and pending ones) survive a
 * desktop-app reload. The user explicitly asked May 24, 2026 — "se
 * poi il file è creato lo continuo a vedere?" — without persistence the
 * inline action chip in chat history disappears on every relaunch.
 *
 * Hygiene on rehydrate (avoid an unbounded blob — audit May 25, 2026):
 *   - drop entries older than ``MAX_AGE_DAYS`` (TTL by ``createdAt``)
 *   - drop transient ``executing`` / ``confirming`` states (in-flight
 *     across a reload is impossible to resume safely)
 *   - cap ``order.length`` at ``MAX_ENTRIES`` (LRU by insertion order;
 *     oldest entries are evicted first)
 *
 * Versioning + migration: ``version: 1`` so a future schema break (e.g.
 * splitting executedData per action type) can be handled cleanly.
 */
const STORAGE_KEY = "ando.activeActions";
const STORAGE_VERSION = 1;
const MAX_AGE_DAYS = 30;
const MAX_ENTRIES = 200;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

function prunePersistedState(
  state: Pick<ActiveActionState, "actions" | "order"> | undefined,
): Pick<ActiveActionState, "actions" | "order"> {
  if (!state) return { actions: {}, order: [] };
  const cutoff = Date.now() - MAX_AGE_DAYS * MS_PER_DAY;
  const keptOrder: string[] = [];
  const keptActions: Record<string, ActiveAction> = {};
  for (const id of state.order) {
    const a = state.actions[id];
    if (!a) continue;
    // Drop transients: a reloaded ``executing`` / ``confirming`` cannot
    // resume the in-flight mutation safely. Surface as still-pending
    // ``proposed`` so the user can click Conferma again from history.
    const safeStatus: ActionDrawerStatus =
      a.status === "executing" || a.status === "confirming" ? "proposed" : a.status;
    const createdMs = Date.parse(a.createdAt);
    if (Number.isFinite(createdMs) && createdMs < cutoff) continue;
    keptOrder.push(id);
    keptActions[id] = { ...a, status: safeStatus };
  }
  // LRU evict from the head (oldest insertions) if still over cap.
  while (keptOrder.length > MAX_ENTRIES) {
    const dropId = keptOrder.shift();
    if (dropId) delete keptActions[dropId];
  }
  return { actions: keptActions, order: keptOrder };
}

export const useActiveActionStore = create<ActiveActionState>()(
  persist(
    (set) => ({
      actions: {},
      order: [],
      upsert: (action) =>
        set((state) => {
          const exists = state.actions[action.id] !== undefined;
          return {
            actions: { ...state.actions, [action.id]: action },
            order: exists ? state.order : [...state.order, action.id],
          };
        }),
      patch: (id, patch) =>
        set((state) => {
          const cur = state.actions[id];
          if (!cur) return state;
          return {
            actions: { ...state.actions, [id]: { ...cur, ...patch } },
          };
        }),
      remove: (id) =>
        set((state) => {
          const next = { ...state.actions };
          delete next[id];
          return {
            actions: next,
            order: state.order.filter((x) => x !== id),
          };
        }),
      clear: () => set({ actions: {}, order: [] }),
    }),
    {
      name: STORAGE_KEY,
      version: STORAGE_VERSION,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ actions: state.actions, order: state.order }),
      // ``onRehydrateStorage`` runs once after localStorage → memory
      // bootstrap. We prune in-place so the in-memory state matches the
      // hygiene rules from the first render.
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        const pruned = prunePersistedState(state);
        state.actions = pruned.actions;
        state.order = pruned.order;
      },
    },
  ),
);

// Exported for unit testing the rehydrate hygiene without spinning up
// the full Zustand persist pipeline.
export const __prunePersistedStateForTests = prunePersistedState;

// ── Selectors ─────────────────────────────────────────────────────────────

export const selectActiveActions = (state: ActiveActionState): readonly ActiveAction[] => {
  const out: ActiveAction[] = [];
  for (const id of state.order) {
    const a = state.actions[id];
    if (a) out.push(a);
  }
  return out;
};

export const selectActionsByTab =
  (tab: string) =>
  (state: ActiveActionState): readonly ActiveAction[] => {
    // Note: tab resolution lives in `actionTypeToTab.ts`; the store stays
    // tab-agnostic and the consumer filters at read time. Re-importing it
    // here would create a cycle since `useActionRouter` already depends on
    // both modules.
    return Object.values(state.actions).filter((a) =>
      a.actionType ? tabMatches(a.actionType, tab) : false,
    );
  };

// Inlined to avoid cycle with `actionTypeToTab.ts` (which imports the store
// in some patterns); the surface uses the typed helper directly.
function tabMatches(_actionType: string, _tab: string): boolean {
  return true; // placeholder — real filtering is done at surface mount time
}

/**
 * Curried selector: actions whose ``queryId`` matches the given chat
 * ``query_id``. Drives the inline action chip below each agent message
 * so the user can re-open a proposal (or jump to the resulting doc)
 * straight from chat history.
 *
 * Returns an empty array when ``queryId`` is null — the inline chip
 * surface will render nothing in that case.
 */
export const selectActionsByQuery =
  (queryId: string | null) =>
  (state: ActiveActionState): readonly ActiveAction[] => {
    if (!queryId) return EMPTY_ACTIONS;
    const out: ActiveAction[] = [];
    for (const id of state.order) {
      const a = state.actions[id];
      if (a?.queryId === queryId) out.push(a);
    }
    return out.length === 0 ? EMPTY_ACTIONS : out;
  };

// Stable empty reference — avoids a new array per render when no
// matching actions exist (memo-friendly for the inline chip surface).
const EMPTY_ACTIONS: readonly ActiveAction[] = [];
